import { type ReactNode, useCallback } from 'react';

import { TreeItemButton, treeItem } from '@air/component-tree';
import { BoardMove, Check } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';

import { PrivateBoardSearch, type PrivateBoardSearchProps } from '~/components/BoardSearch/PrivateBoardSearch';
import { useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { BOARDS_SELECT_ROOT_OPTION } from '~/constants/BoardSearch';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { canChangeBoardDetails } from '~/utils/permissions/boardPermissions';

export type RecentlyDeletedNewParentSelectionModalProps = (
  | {
      onConfirm: (
        parent?:
          | {
              boardId: string;
            }
          | 'root',
      ) => Promise<void>;
      canSelectLibrary?: false;
    }
  | {
      onConfirm: (
        parent?:
          | {
              boardId: string;
            }
          | {
              libraryId: string;
            }
          | 'root',
      ) => Promise<void>;
      canSelectLibrary: true;
    }
) & {
  description: ReactNode;
  rootOptionLabel: string;
};

export const RecentlyDeletedNewParentSelectionModal = ({
  description,
  rootOptionLabel,
  canSelectLibrary,
  onClose,
  onConfirm,
}: AirModalProps<RecentlyDeletedNewParentSelectionModalProps>) => {
  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const { getBoardPermissions } = useBoardPermissionsCache();

  const {
    selectedBoard,
    selectedLibrary,
    isBoardSelected,
    shouldShowSubBoards,
    onSelectBoard,
    isLibrarySelected,
    shouldShowLibraryBoards,
    onSelectLibrary,
  } = useSingleBoardSearchState();

  const onBoardSelectChange: PrivateBoardSearchProps['onBoardSelectChange'] = useCallback(
    ({ board, selected }) => {
      onSelectBoard(selected ? board : null);
    },
    [onSelectBoard],
  );

  const getIsBoardDisabled: Required<PrivateBoardSearchProps>['getIsBoardDisabled'] = useCallback(
    (board) => {
      const boardPermissions = getBoardPermissions(board.id);

      if (!canChangeBoardDetails(boardPermissions)) {
        return {
          isDisabled: true,
          message: 'You do not have permission to edit this board',
        };
      }
    },
    [getBoardPermissions],
  );

  const handleConfirm = useCallback(() => {
    if (canSelectLibrary && selectedLibrary?.id) {
      onConfirm({
        libraryId: selectedLibrary.id,
      });
    } else if (selectedBoard?.id) {
      onConfirm(
        selectedBoard.id === 'root'
          ? 'root'
          : {
              boardId: selectedBoard.id,
            },
      );
    } else {
      onConfirm();
    }

    onClose();
  }, [onClose, onConfirm, selectedBoard, selectedLibrary, canSelectLibrary]);

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  return (
    <Modal dangerouslyBypassFocusLock isOpen onDismiss={onClose}>
      <div className="mb-3 flex justify-between">
        <ModalTitle>Select recovery location</ModalTitle>
        <ModalCloseButton className="shrink-0" onClick={onClose} />
      </div>
      <ModalDescription className="mb-4">{description}</ModalDescription>
      <PrivateBoardSearch
        isBoardSelected={isBoardSelected}
        onBoardSelectChange={onBoardSelectChange}
        shouldShowSubBoards={shouldShowSubBoards}
        isLibrarySelected={isLibrarySelected}
        shouldShowLibraryBoards={shouldShowLibraryBoards}
        onSelectLibrary={onSelectLibrary}
        shouldExpandLibraryInsteadOfSelect={!canSelectLibrary}
        getIsBoardDisabled={getIsBoardDisabled}
      >
        <div
          data-testid="MOVE_ITEMS_MODAL_MOVE_TO_TOP_LEVEL"
          className={treeItem({
            size: 'small',
            state: isBoardSelected(BOARDS_SELECT_ROOT_OPTION) ? 'selected' : 'default',
          })}
        >
          <BoardMove className="size-4 shrink-0" />

          <TreeItemButton onClick={() => onSelectBoard(BOARDS_SELECT_ROOT_OPTION)}>{rootOptionLabel}</TreeItemButton>

          {isBoardSelected(BOARDS_SELECT_ROOT_OPTION) && (
            <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-blue-3">
              <Check className="size-3 text-blue-11" />
            </div>
          )}
        </div>
      </PrivateBoardSearch>
      <div className="mt-6 flex justify-end gap-3">
        <Button appearance="ghost" color="grey" onClick={onClose} size="large">
          Cancel
        </Button>
        <Button
          appearance="filled"
          color="blue"
          disabled={!selectedLibrary?.id && !selectedBoard?.id}
          size="large"
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </div>
    </Modal>
  );
};
